import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Edit, Trash, Ban } from "lucide-react";
import { toast } from "sonner";
import { UserTableProps } from "./types";
import { Badge } from "@/components/ui/badge";
import { ProductLimitCell } from "./ProductLimitCell";

export const UserTable = ({ owners, isLoading, onBlockUser }: UserTableProps) => {
  const { data: businessTypes } = useQuery({
    queryKey: ['businessTypes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('business_types')
        .select('*');
      
      if (error) throw error;
      return data;
    }
  });

  const { data: subscriptions, refetch: refetchSubscriptions } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*');
      
      if (error) throw error;
      return data;
    }
  });

  const { data: customLimits, refetch: refetchCustomLimits } = useQuery({
    queryKey: ['customLimits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('custom_product_limits')
        .select('*');
      
      if (error) throw error;
      return data;
    }
  });

  const getBusinessTypeName = (business_type_id: string) => {
    const businessType = businessTypes?.find(bt => bt.id === business_type_id);
    return businessType?.name || 'N/A';
  };

  const getUserSubscription = (userId: string) => {
    const subscription = subscriptions?.find(sub => sub.owner_id === userId);
    return subscription?.plan || 'free';
  };

  const getProductLimit = (userId: string) => {
    const customLimit = customLimits?.find(limit => limit.owner_id === userId);
    if (customLimit) {
      return customLimit.product_limit;
    }
    
    const subscription = subscriptions?.find(sub => sub.owner_id === userId);
    if (!subscription) return 3; // Default free plan limit
    
    switch (subscription.plan) {
      case 'standard':
        return 20;
      case 'premium':
        return -1; // Unlimited
      default:
        return 3; // Free plan
    }
  };

  const handleDelete = async (userId: string) => {
    toast.info("Fonctionnalité à venir - Suppression");
  };

  const handleUpdateLimit = async (userId: string, newLimit: number) => {
    try {
      const { error: upsertError } = await supabase
        .from('custom_product_limits')
        .upsert({
          owner_id: userId,
          product_limit: newLimit
        });

      if (upsertError) throw upsertError;

      await refetchCustomLimits();
      toast.success("Limite de produits mise à jour avec succès");
    } catch (error: any) {
      console.error("Erreur lors de la mise à jour de la limite:", error);
      toast.error("Erreur lors de la mise à jour de la limite");
    }
  };

  // Filter to show only active users
  const activeOwners = owners.filter(owner => !owner.is_blocked);

  const columns = [
    {
      id: "name",
      label: "Nom",
      cell: (row: any) => row.full_name || "N/A",
    },
    {
      id: "business_type",
      label: "Type d'entreprise",
      cell: (row: any) => getBusinessTypeName(row.owner_data?.business_type_id),
    },
    {
      id: "subscription",
      label: "Abonnement",
      cell: (row: any) => (
        <Badge variant={getUserSubscription(row.id) === 'free' ? 'default' : 'secondary'}>
          {getUserSubscription(row.id)}
        </Badge>
      ),
    },
    {
      id: "product_limit",
      label: "Limite de produits",
      cell: (row: any) => (
        <ProductLimitCell
          userId={row.id}
          subscriptionPlan={getUserSubscription(row.id)}
          currentLimit={getProductLimit(row.id)}
          onUpdateLimit={handleUpdateLimit}
        />
      ),
    },
    {
      id: "status",
      label: "Statut",
      cell: (row: any) => (
        <Badge variant={row.is_blocked ? "destructive" : "outline"}>
          {row.is_blocked ? "Bloqué" : "Actif"}
        </Badge>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      cell: (row: any) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => toast.info("Fonctionnalité à venir - Édition")}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDelete(row.id)}
          >
            <Trash className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onBlockUser(row.id, !row.is_blocked)}
          >
            <Ban className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return <DataTable columns={columns} data={activeOwners} />;
};